interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

export type PwaInstallOutcome = "accepted" | "dismissed" | "unavailable";

type Listener = () => void;

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<Listener>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

// The browser may fire this before React mounts, so it is captured at module load.
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    emit();
  });
}

export const pwaInstallStore = {
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  canInstall(): boolean {
    return deferredPrompt !== null;
  },

  async promptInstall(): Promise<PwaInstallOutcome> {
    const event = deferredPrompt;
    if (!event) return "unavailable";
    // A captured prompt can only be shown once.
    deferredPrompt = null;
    emit();
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  },
};
